import {
  MAX_MARKERS_BREAKPOINT,
  MAX_MARKERS_DESKTOP,
  MAX_MARKERS_MOBILE,
} from '../../constants'

function producers(state) {
  return Object.keys(state.cube.producers).map((cubeName) => {
    return {
      cubeName: cubeName,
      ...state.cube.producers[cubeName],
    }
  })
}

function consumers(state) {
  return Object.keys(state.cube.consumers).map((cubeName) => {
    return {
      cubeName: cubeName,
      ...state.cube.consumers[cubeName],
    }
  })
}

function contributors(state) {
  let contributors = {}
  Object.keys(state.cube.consumers).map((cubeName) => {
    const cube = state.cube.consumers[cubeName]
    if (cube.isContributor) {
      contributors[cubeName] = cube
    }
  })

  Object.keys(state.cube.producers).map((cubeName) => {
    const cube = state.cube.producers[cubeName]
    if (cube.isContributor) {
      contributors[cubeName] = cube
    }
  })

  return Object.entries(contributors).map((row) => {
    return row[1]
  })
}

function donatees(state) {
  let donatees = {}
  Object.keys(state.cube.consumers).map((cubeName) => {
    const cube = state.cube.consumers[cubeName]
    if (cube.isDonatee) {
      donatees[cubeName] = cube
    }
  })

  Object.keys(state.cube.producers).map((cubeName) => {
    const cube = state.cube.producers[cubeName]
    if (cube.isDonatee) {
      donatees[cubeName] = cube
    }
  })

  return Object.entries(donatees).map((row) => {
    return row[1]
  })
}

// The following 2 functions are copied and slightly modified from
// https://stackoverflow.com/questions/1502590/calculate-distance-between-two-points-in-google-maps-v3
// to calculate the distance in m between 2 coordinates.
// This functionality is to be considered inexact and not necessarily a permanent
// and scalable solution.
function rad(x) {
  return (x * Math.PI) / 180
}

function getDistance(p1, p2) {
  // Earth’s mean radius in meter
  const earthRadius = 6378137
  const dLat = rad(p2.lat - p1.lat)
  const dLong = rad(p2.lng - p1.lng)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(rad(p1.lat)) *
      Math.cos(rad(p2.lat)) *
      Math.sin(dLong / 2) *
      Math.sin(dLong / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  // Returns the distance in meter.
  return earthRadius * c
}

function allCubes(state) {
  return state.map.globalCubesList
}

function allCubesByDistanceFrom(state, getters) {
  return ({
    latitude = 'default',
    longitude = 'default',
    order = 'ascending',
    limit = window.innerWidth > MAX_MARKERS_BREAKPOINT
      ? MAX_MARKERS_DESKTOP
      : MAX_MARKERS_MOBILE,
    filterFunction = getters.filterFunction,
  }) => {
    if (latitude === 'default') {
      latitude = state.map.center.latitude
    }

    if (longitude === 'default') {
      longitude = state.map.center.longitude
    }

    const currentCenter = {
      lat: latitude,
      lng: longitude,
    }

    const result = state.map.globalCubesList
      .map((cube) => {
        const coordinate = {
          lat: cube.params.latitude,
          lng: cube.params.longitude,
        }

        cube.distance = getDistance(currentCenter, coordinate)
        cube.distanceFromMe = getDistance(homeCoordinates(state), coordinate)

        return cube
      })
      .sort((cubeA, cubeB) => {
        if (order === 'ascending') {
          return cubeA.distance - cubeB.distance
        } else {
          return cubeB.distance - cubeA.distance
        }
      })
      .filter((cube) => filterFunction(cube, getters))

    // Sort so the sidebar has a stable order
    return result.slice(0, limit).sort((cubeA, cubeB) => {
      return cubeA.cubeName > cubeB.cubeName
    })
  }
}

function isCubeSelected(state) {
  return !!state.cube.name
}

function cubeName(state) {
  return state.cube.name
}

function nickName(state) {
  return state.cube.nickName
}

function availableCubes(state) {
  return state.availableCubes
}

function cubeIconUrl(state) {
  return (cube, imgType = 'png') => {
    // Default to our own cube.
    if (cube === false)
      cube = allCubes(state).find((cube) => cube.cubeName === state.cube.name)

    if (cube && cube.icon) {
      if (cube.icon.isCustom) {
        return cube.icon.fileName
      } else {
        return `${cube.icon.urlBase}${cube.icon.fileName}.${imgType}`
      }
    }
  }
}

function unusedEnergyLines(state) {
  return state.cube.unusedEnergyLines
}

function openConnectionsAvailable(state) {
  return state.cube.openConnectionsAvailable
}

/**
 * Checks whether a given cube is a producer to the current user.
 * Note that we check the status on the cube, but also whether or not the cube
 * is in the list of producers.
 *
 * @param state
 * @returns boolean
 */
function isProducerToMe(state) {
  return (cube) => {
    if (cube.params.nickName === state.cube.nickName || !cube.isProducer) {
      return false
    }
    return !!state.cube.producers.find((producer) => {
      return producer.cubeName === cube.cubeName
    })
  }
}

function connectionPriority() {
  return (cube) => {
    switch (cube.tradingDetails.priority) {
      case 1:
        return 'high'

      case 2:
        return 'default'

      case 3:
        return 'low'
    }
  }
}

function defaultSellingPrices(state) {
  return {
    currentPrice: state.settings.producerSettings.defaultSellingPrice,
    nextPrice: state.settings.producerSettings.nextDefaultSellingPrice.price,
    dateNextPrice: state.settings.producerSettings.nextDefaultSellingPrice.date,
  }
}

function historyDataReceived(state) {
  return state.historyData.received === true
}

function chartData(state) {
  return {
    labels: state.historyData.labels,
    datasets: state.historyData.datasets,
  }
}

function homeCoordinates(state) {
  return {
    lat: state.cube.latitude,
    lng: state.cube.longitude,
  }
}

function socketUrl(state) {
  return `${state.cube.remoteHostName}:${state.cube.remoteWebSocketPort}${state.cube.remoteWebSocketPath}`
}

function connectionPriorities(state) {
  return state.cube.connectionPriorities
}

function hasProduction(state) {
  return state.cube.hasProduction
}

function energyType(state) {
  if (state.cube.hasProduction) {
    return state.cube.productionType
  } else {
    return 'consumer'
  }
}

function getCubeByName(state) {
  return (cubeName) => {
    return state.map.globalCubesList.find((cube) => {
      return cube.cubeName === cubeName
    })
  }
}

function cubeConnectionsReceived(state) {
  return state.cube.connectionsReceived
}

function notificationsForCube(state) {
  return (cubeName) => {
    return state.cube.notifications.filter((n) => {
      return n.triggeringcubename === cubeName
    })
  }
}

function cubeDescription(state) {
  return state.cube.descriptionText
}

function images(state) {
  return state.cube.images
}

function originalImage(state) {
  if (Object.keys(state.cube.images).length > 0) {
    return state.cube.images.original.url
  }
  return false
}

function imageCropData(state) {
  if (state.cube.images) {
    return state.cube.images.cropData
  }
  return false
}

function tradingStatistics(state) {
  return (dataType) => {
    return state.tradingStatistics.datasets[dataType]
  }
}

function tradingStatisticsReceived(state) {
  return state.tradingStatistics.received
}

function historySelectedDate(state) {
  return state.historySelectedDate
}

function tradingStatisticsLastUpdate(state) {
  return state.tradingStatistics.lastUpdate
}

function tradingStatisticsAggregate(state) {
  return (aggregate) => {
    return state.tradingStatistics[aggregate]
  }
}

function showBasePricePopup(state) {
  return (
    state.cube.hasProduction &&
    state.settings.producerSettings.defaultSellingPrice === false
  )
}

function supplierData(state) {
  return state.cube.supplier
}

function mapStyles(state) {
  return state.map.styles
}

export default {
  producers,
  consumers,
  donatees,
  contributors,
  allCubesByDistanceFrom,
  allCubes,
  cubeName,
  nickName,
  isCubeSelected,
  availableCubes,
  cubeIconUrl,
  unusedEnergyLines,
  isProducerToMe,
  connectionPriority,
  defaultSellingPrices,
  historyDataReceived,
  historySelectedDate,
  chartData,
  homeCoordinates,
  socketUrl,
  connectionPriorities,
  hasProduction,
  getCubeByName,
  openConnectionsAvailable,
  cubeConnectionsReceived,
  notificationsForCube,
  images,
  originalImage,
  imageCropData,
  cubeDescription,
  tradingStatistics,
  tradingStatisticsReceived,
  tradingStatisticsLastUpdate,
  tradingStatisticsAggregate,
  showBasePricePopup,
  mapStyles,
  supplierData,
  energyType,
}
